import {useCallback, useEffect, useMemo, useState} from "react";
import { useLocation } from "react-router-dom";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import useSettings from "../hooks/useSettings";
// components
import Button from "./Button";
import { TermsAndConditions } from "./TermsAndConditions";
// api
import { createPurchaseByQuote, startPurchaseByQuote } from "../api/purchaseApi";
import stripeLogo from "../assets/stripe.logo.svg";
// @interfaces
import { IPurchase } from "../@interfaces/purchase.interface";
import { IQuote } from "../@interfaces/quote.interface";
import { IMerchantConfig } from "../@interfaces/config.interface";
// utils
import { formatPriceTextOnly } from "../utils/priceFormatter";
// styles
import styles from "../styles/pages/payment/CheckoutForm.module.css";
import { sendAnalyticsEvent } from "../utils/analytics";
import {StripePaymentRequestButtonElementOptions} from "@stripe/stripe-js";
import GoogleApplePay from "./inputs/GoogleApplePay";
import Select from "./Select";
import { ISelectedParams } from "../@interfaces/common.interface";

interface CheckoutFormProps {
  programmeCode?: string | null;
  quote: IQuote;
  onCheckoutSuccess: (purchase: IPurchase) => void;
  setUserEmail: (email: string) => void;
  disableCurrencyList: boolean;
  SelectComponent: React.ReactNode;
  merchantConfig: IMerchantConfig;
  setSelectedParams: (params: any) => void;
  selectedParams: ISelectedParams;
  onOffsetClick?: (params: ISelectedParams) => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  programmeCode,
  quote,
  onCheckoutSuccess,
  setUserEmail,
  disableCurrencyList,
  SelectComponent,
  merchantConfig,
  setSelectedParams,
  selectedParams,
  onOffsetClick,
}) => {
  const search = new URLSearchParams(useLocation().search);
  const merchantCode = search.get("merchantCode") || "CARBONCLICK";

  const stripe = useStripe();
  const elements = useElements();
  const { language } = useSettings();

  const [paymentError, setPaymentError] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [hasPreviouslyEditedEmail, setHasPreviouslyEditedEmail] = useState(false);
  const [hasPreviouslyEditedCardNumber, setHasPreviouslyEditedCardNumber] = useState(false);
  const [hasPreviouslyEditedExpiry, setHasPreviouslyEditedExpiry] = useState(false);
  const [hasPreviouslyEditedCvc, setHasPreviouslyEditedCvc] = useState(false);
  const [receiveCommunications, setReceiveCommunications] = useState(false);

  const [appleGooglePayPaymentRequest, setAppleGooglePayPaymentRequest] = useState<StripePaymentRequestButtonElementOptions | null>(null); //FOR GOOGLE/APPLE PAY

  const [cardError, setCardError] = useState([
    { type: "cardNumber", complete: false, msg: "Please enter a valid card number" },
    { type: "cardExpiry", complete: false, msg: "Card expiry is invalid" },
    { type: "cardCvc", complete: false, msg: "Card CVC is invalid" },
    { type: "email", complete: false, msg: "Email is invalid", invalid: false },
  ]);


  const setPaymentErrorAndRemoveLoading = useCallback((errorText: string) => {
    setIsLoading(false);
    setPaymentError({ message: errorText });
  }, []);

  const handleStripePaymentResponse = useCallback(async (stripeResponse: any, email: string) => {
    if (stripeResponse?.paymentIntent?.status === "succeeded") {
      let page;
      if (window.location !== window.parent.location && document.referrer) {
        const referrerUrl = new URL(document.referrer); // document.referrer never contains query string
        referrerUrl.search = document.location.search;  // so we use search from iframe url
        page = referrerUrl.href;
      } else {
        page = document.location.href;
      }
      const purchaseData = {
        programmeCode: programmeCode,
        quoteId: quote.quoteId,
        emailAddress: email,
        paymentIntentId: stripeResponse.paymentIntent.id,
        paymentProvider: "STRIPE",
        paymentProviderToken: null,
        metadata: {
          merchant_code: merchantCode,
          receive_communications: receiveCommunications,
          acquisition: { page },
        }
      };

      const confirmPaymentApiResponse = await createPurchaseByQuote(purchaseData, language);

      if (confirmPaymentApiResponse?.data?.purchaseId) {
        setIsLoading(false);
        onCheckoutSuccess(confirmPaymentApiResponse.data);
      }
    }
  }, [programmeCode, quote, language, onCheckoutSuccess, receiveCommunications, merchantCode]);

  const handleAppleGooglePayTriggered = useCallback(async (stripe: any, event: any) => {
    try{
      const intentResponse = await startPurchaseByQuote({quoteId: quote.quoteId});

      let confirmResponse = await stripe.confirmCardPayment(
          intentResponse.data.clientSecret,
          {payment_method: event.paymentMethod.id},
          {handleActions: false}
      )

      if(confirmResponse?.error?.message){
        event.complete('fail');
        setPaymentErrorAndRemoveLoading(confirmResponse.error.message);
      }
      else {
        event.complete('success'); // Done - close the browser payment method interface
        if (confirmResponse.paymentIntent.status === "requires_action") { //let Stripe.js handle flow if action required (3d secure?)
          confirmResponse = await stripe.confirmCardPayment(intentResponse.data.clientSecret);
          if (confirmResponse.error) {
            setPaymentErrorAndRemoveLoading(confirmResponse.error?.message || "There was a problem with your payment, please try again");
          } else {
            await handleStripePaymentResponse(confirmResponse, event.payerEmail);
          }
        } else {
          await handleStripePaymentResponse(confirmResponse, event.payerEmail);
        }
      }
    }catch(e){
      event?.complete('fail');
      setPaymentErrorAndRemoveLoading('An error occured, please try a different payment method');
    }
  }, [quote, setPaymentErrorAndRemoveLoading, handleStripePaymentResponse]);

  useEffect(() => {
    if(quote?.offsetPrice){
      if (stripe) {
        const pr = stripe.paymentRequest({
          country: 'NZ',
          currency: quote.offsetPrice.currency.toLowerCase(),
          total: {
            label: 'Your offset',
            amount: Math.round(quote.offsetPrice.zeroDecimalCurrency ? quote.offsetPrice.value : quote.offsetPrice.value*100),
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        // Check the availability of the Payment Request API.
        if(pr && stripe) {
          pr.canMakePayment().then(result => {
            if (result) {
              // @ts-ignore
              setAppleGooglePayPaymentRequest(pr);
              pr.on('paymentmethod', (event) => { handleAppleGooglePayTriggered(stripe, event) });
            }
          });
        }
      }
    }
    // @ts-ignore
  },[quote, stripe, handleAppleGooglePayTriggered]);

  const handleChange = (card: any) => {
    setPaymentError(null);
    let error = [...cardError];

    if (card.elementType === "cardNumber") {
      error[0].complete = card.complete;
      setCardError(error);
    } else if (card.elementType === "cardExpiry") {
      error[1].complete = card.complete;
      setCardError(error);
    } else if (card.elementType === "cardCvc") {
      error[2].complete = card.complete;
      setCardError(error);
    } else if (card.elementType === "email") {
      error[3].complete = card.complete;
      error[3].invalid = card.invalid;
      setCardError(error);
    }
  };

  const validateCardDetails = () => {
    const result = cardError.map((card) => (card.complete === false ? 1 : 0));
    return result.reduce((acc: any, currVal) => {
      return acc + currVal;
    }, 0);
  };

  const handleEmailChange = (event: any) => {
    let complete = false;
    let invalid = false;
    if (event.target.value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
      complete = true;
    } else {
      invalid = true;
    }
    setEmail(event.target.value);
    handleChange({
      elementType: "email",
      complete: complete,
      invalid: invalid,
    });
  };

  const startPaymentByQuote = async () => {
    sendAnalyticsEvent('add_payment_info', { 
        co2Kgs: quote.carbonOffsetQuantity.value.toString() as string, itemName: "Journey Offset", price: quote.offsetPrice });
    const startPurchaseByQuoteResponse = await startPurchaseByQuote({
      quoteId: quote.quoteId,
    }).catch((error) => {
      console.log("Start payment by quote fails by" + error?.errors[0]?.message);
      setPaymentErrorAndRemoveLoading(error?.errors[0]?.message || "Something went wrong");
    });
    return startPurchaseByQuoteResponse?.data?.clientSecret;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setUserEmail(email);
    setIsLoading(true);
    const clientSecret = await startPaymentByQuote();
    if(!clientSecret) {
      return;
    }
    let confirmResponse = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          // @ts-ignore
          card: elements.getElement(CardNumberElement),
          billing_details: {
            email,
          },
        },
      },
      { handleActions: false }
    );

    if (confirmResponse?.paymentIntent?.status === "requires_action") {
      confirmResponse = await stripe.confirmCardPayment(clientSecret);

      if (confirmResponse.error) {
        setPaymentErrorAndRemoveLoading(confirmResponse.error?.message || "There was a problem with your payment, please try again");
      } else {
        handleStripePaymentResponse(confirmResponse, email);
      }
    } else {
      handleStripePaymentResponse(confirmResponse, email);
    }

    if (confirmResponse?.error) {
      setPaymentErrorAndRemoveLoading(
        confirmResponse?.error?.message || "Something went wrong"
      );
    }
  };

  const handleCardElementReady = () => {
    setIsLoading(false);
  };

  const errorColor = '#ff4842';

  const createOptions = () => {
    return {
      placeholder: '',
      style: {
        invalid: {
          color: errorColor,
        },
        base: {
          fontFamily: merchantConfig?.fontConfig?.body,
          textAlign: language === 'ar' ? 'right' : 'left',
        }
      },
    };
  };

  const baseBorderStyle = "1px solid rgba(145, 158, 171, 0.32)";
  const invalidBorderStyle = `2px solid ${errorColor}`;

  const emailErrMessage = hasPreviouslyEditedEmail && !cardError[3].complete && cardError[3].msg;
  const cardNumberErrMessage = hasPreviouslyEditedCardNumber && !cardError[0].complete && cardError[0].msg;
  const expiryErrMessage = hasPreviouslyEditedExpiry && !cardError[1].complete && cardError[1].msg;
  const cvcErrMessage = hasPreviouslyEditedCvc && !cardError[2].complete && cardError[2].msg;

  const formGap = { gap: disableCurrencyList ? '2rem' : '1.5rem' };

  const ratioOptions = useMemo(() => {
    return merchantConfig?.ratioSelection ? merchantConfig?.ratioSelection.map(r => ({ value: r, label: `${r * 100}%` })) : [];
  }, [merchantConfig?.ratioSelection]);

  const defaultOffsetRatio = useMemo(() => {
    return ratioOptions.find(r => r.value === selectedParams?.offsetRatio);
  }, [ratioOptions, selectedParams?.offsetRatio]);

  return (
    <>
    <form
      id="stripePaymentForm"
      className={styles.stripePaymentForm}
      onSubmit={handleSubmit}
    >
      <div style={{ ...formGap }} className={styles.formContent}>

        <p className={styles.secure}>Pay by secure credit card</p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!disableCurrencyList &&
            <div className={`${styles.selectWrapper}`} style={{ flex: 1 }}> 
              {SelectComponent}
              <Select
                selectedItem={defaultOffsetRatio}
                onChange={(v) => {
                  setSelectedParams((prev: ISelectedParams) => ({ ...prev, offsetRatio: v?.value }));
                  onOffsetClick && onOffsetClick({ offsetRatio: v?.value });
                }}
                optionsList={ratioOptions}
                label="Offset a little less or more"
                noTranslate
              />
            </div>
          }
        </div>
        <div style={{width: '100%'}}>
          <GoogleApplePay paymentRequest={appleGooglePayPaymentRequest} />
        </div>

        <div>
          <div
            className={styles.inputWrapper}
            style={{
              border: emailErrMessage ? invalidBorderStyle : baseBorderStyle,
            }}
          >
            <label className={styles.inputLabel}
              style={{ color: emailErrMessage ? errorColor : "inherit" }}
            >
              {emailErrMessage || "Email address"}
            </label>
            <input
              id="email-element"
              type="email"
              autoComplete="email"
              name="email"
              className="cardInput"
              onChange={handleEmailChange}
              onBlur={() => setHasPreviouslyEditedEmail(true)}
              style={{
                ...(!cardError[3].complete && hasPreviouslyEditedEmail
                  ? { color: errorColor }
                  : null),
              }}
            />
          </div>
        </div>

        <div>
          <div className={styles.inputWrapper}
            style={{
              border: cardNumberErrMessage ? invalidBorderStyle : baseBorderStyle,
            }}
          >
          <label
            className={styles.inputLabel}
            style={{ color: cardNumberErrMessage ? errorColor : "inherit" }}
          >
            {cardNumberErrMessage || "Card number"}
          </label>
            <CardNumberElement
              onChange={handleChange}
              onReady={handleCardElementReady}
              options={createOptions()}
              onBlur={() => setHasPreviouslyEditedCardNumber(true)}
            />
          </div>
        </div>

        <div>
        <div style={{ display: "flex", gap: "2rem" }}>
          <div style={{ width: "50%" }}>
            <div
              className={styles.inputWrapper}
              style={{
                border: expiryErrMessage ? invalidBorderStyle : baseBorderStyle,
              }}
            >
              <label className={styles.inputLabel}
                style={{ color: expiryErrMessage ? errorColor : "inherit" }}
              >
                {expiryErrMessage || "Expiry date (MM/YY)"}
              </label>
              <CardExpiryElement
                onChange={handleChange}
                options={createOptions()}
                onBlur={() => setHasPreviouslyEditedExpiry(true)}
              />
            </div>
          </div>

          <div style={{ width: "50%" }}>
            <div
              className={styles.inputWrapper}
              style={{ border: cvcErrMessage ? invalidBorderStyle : baseBorderStyle }}
            >
              <label
                className={styles.inputLabel}
                style={{ color: cvcErrMessage ? errorColor : "inherit" }}
              >
                {cvcErrMessage || "Security code"}
              </label>
              <CardCvcElement
                onChange={handleChange}
                options={createOptions()}
                onBlur={() => setHasPreviouslyEditedCvc(true)}
              />
            </div>
          </div>
        </div>

        <div className={styles.stripeMessage}>
          <p>100% Secure payments provided by</p>
          <img
            style={{ height: 22, width: "auto", paddingBottom: 2 }}
            src={stripeLogo}
            alt="stripe payment logo"
          />
        </div>

        {merchantConfig.showMarketingConsent && merchantConfig.marketingConsentText && (
          <div className={styles.checkboxContainer}>
            <label>
              <input
                checked={receiveCommunications}
                onChange={() => setReceiveCommunications(prev => !prev)}
                className={styles.checkbox}
                type="checkbox"
              />
            </label>

            <p className={styles.marketingConsentText}>{merchantConfig.marketingConsentText}</p>
          </div>
        )}
      </div>
      </div>

      <div style={{ color: '#ff4842', fontSize: '0.75rem', textAlign: 'right' }}>
        {paymentError?.message && paymentError.message}
      </div>

      <div>
        <Button
          disabled={!(validateCardDetails() === 0 && !isLoading)}
          id="submit"
          isLoading={isLoading}
          variant='filled'
          text={`Pay ${formatPriceTextOnly(quote?.offsetPrice, true)}`}
        />

        <TermsAndConditions merchantConfig={merchantConfig} />
      </div>
    </form>
    </>
  );
};

export default CheckoutForm;
